/**
 * Pure geometry for the inline-SVG charts (design-reference §5). Every function
 * reproduces the exact maths from the mock, so a given input yields the exact
 * `points` / `d` / `dasharray` string the design specifies. Kept free of React so
 * the maths is unit-testable in isolation.
 */

/** `fmtK`: 1,234 → "1.2k", 41,286 → "41k", < 1000 → rounded integer. */
export function fmtK(n: number): string {
  if (n >= 1000) return (n / 1000).toFixed(n >= 10000 ? 0 : 1) + 'k';
  // A fractional value must keep its fraction. Rounding to an integer is right
  // for a count but destroys a rate or an average: a classification confidence
  // of 0.93 rendered as "1", which is not a rounding of the number so much as a
  // deletion of it. Only sub-10 non-integers are affected, so every count the
  // charts have ever shown formats exactly as before.
  if (!Number.isInteger(n) && Math.abs(n) < 10) return n.toFixed(2);
  return String(Math.round(n));
}

/** `fmt`: rounded, en-GB grouped (41286 → "41,286"). */
export function fmt(n: number): string {
  return Math.round(n).toLocaleString('en-GB');
}

// ---------------------------------------------------------------- sparkline --
/**
 * §5.1 — reduce `days` to ≤ 9 stride-means, normalise to 0–1 (optionally
 * inverted), and map to the 80×22 viewBox with `y = 19 − t·15` (y spans 4→19).
 */
export function sparkPoints(days: readonly number[], inv = false): string {
  if (days.length === 0) return '';
  const k = 9;
  const chunk = Math.max(1, Math.floor(days.length / k));
  const vals: number[] = [];
  for (let i = 0; i < days.length; i += chunk) {
    const slice = days.slice(i, i + chunk);
    const mean = slice.reduce((a, b) => a + b, 0) / slice.length;
    vals.push(mean);
  }
  const mx = Math.max(...vals);
  const mn = Math.min(...vals);
  return vals
    .map((v, i) => {
      let t = mx === mn ? 0.5 : (v - mn) / (mx - mn);
      if (inv) t = 1 - t;
      const x = vals.length === 1 ? 0 : (i / (vals.length - 1)) * 80;
      const y = 19 - t * 15;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
}

// ------------------------------------------------------------- volume line --
export interface VolumeGeometry {
  line: string;
  area: string;
  /** Axis top — the smallest "nice" (1/2/5×10^n) ceiling at least as large as
   * the data's own max, so a low-volume tenant isn't plotted against a
   * one-size-fits-all axis it barely occupies. */
  nice: number;
  /** Four y-axis labels top→bottom, at `nice × [1, 0.75, 0.5, 0.25]`. */
  yLabels: string[];
  /** The y (SVG units) each of the four gridlines/labels above sits at — computed
   * from the exact same fractions as `yLabels`, so the two can never drift out
   * of sync (see `LineArea.tsx`, which draws its gridlines from this array
   * rather than a second, independently-hardcoded set of y-coordinates). */
  gridY: number[];
  /** Per-point [x, y] for hit-testing the crosshair. */
  points: [number, number][];
}

/** The plot's pixel geometry: y=168 is the zero baseline, y=30 is the axis top. */
const PLOT_BASELINE_Y = 168;
const PLOT_HEIGHT = 138;

/**
 * Horizontal inset for the first and last point, in `viewBox` units.
 *
 * The series used to run x=0 to x=720 — exactly the edges of the viewBox — so the
 * first and last points were bisected by the clip: half the 2px line stroke gone
 * at each end, a scatter dot cut to a half-moon, and the area fill starting with
 * a vertical wall instead of a point.
 *
 * Sized by the widest thing that can sit *centred* on an end point, which is a
 * bar: `LineArea` caps those at 44 units, so 22 is the minimum that keeps one
 * whole. 24 gives a little air on top of that, and also covers the 4.5-unit
 * scatter dot and the 2-unit line stroke.
 *
 * The axis labels are NOT this margin's job — they live in a fixed-pixel gutter
 * outside the SVG. A viewBox inset scales with the element, so at a narrow width
 * it would shrink below the labels' own size and they would sit on the plot again.
 */
const PLOT_INSET_X = 24;
const PLOT_LEFT_X = PLOT_INSET_X;
const PLOT_RIGHT_X = 720 - PLOT_INSET_X;

/** The fractions of `nice` the four gridlines/labels sit at, top to bottom. */
const AXIS_FRACTIONS = [1, 0.75, 0.5, 0.25];

/**
 * The smallest "nice" ceiling — 1, 2 or 5 × a power of ten — at least as large
 * as `value`. Unlike a flat floor (the previous `Math.ceil(mx / 200) * 200`,
 * which pinned every tenant's axis top at 200+ regardless of how small their
 * actual volume was), this scales down for a low-volume tenant so its line
 * still uses the full chart height instead of hugging the zero baseline.
 */
function niceCeiling(value: number): number {
  if (value <= 0) return 10;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return step * magnitude;
}

/** §5.2 — line/area path + `nice` axis top + y-labels/gridlines. */
export function volumeGeometry(days: readonly number[]): VolumeGeometry {
  const n = days.length;
  const mx = Math.max(...days, 1);
  const nice = niceCeiling(mx);
  const span = PLOT_RIGHT_X - PLOT_LEFT_X;
  const points: [number, number][] = days.map((v, i) => [
    // A single point sits at the middle rather than at an edge: there is no span
    // to distribute it along, and pinning it left made a one-day series look
    // like the start of a line that had been cut off.
    n === 1 ? PLOT_LEFT_X + span / 2 : PLOT_LEFT_X + (i / (n - 1)) * span,
    PLOT_BASELINE_Y - (v / nice) * PLOT_HEIGHT,
  ]);
  let line = '';
  points.forEach(([x, y], i) => {
    line += (i ? ' L' : 'M') + x.toFixed(1) + ',' + y.toFixed(1);
  });
  // Closed along the baseline at the same insets the line uses, or the fill would
  // splay out past where the line starts and ends.
  const first = points[0]?.[0] ?? PLOT_LEFT_X;
  const last = points[points.length - 1]?.[0] ?? PLOT_RIGHT_X;
  const area = `${line} L${last.toFixed(1)},${String(PLOT_BASELINE_Y)} L${first.toFixed(1)},${String(PLOT_BASELINE_Y)} Z`;
  const yLabels = AXIS_FRACTIONS.map((f) => fmtK(nice * f));
  const gridY = AXIS_FRACTIONS.map((f) => PLOT_BASELINE_Y - f * PLOT_HEIGHT);
  return { line, area, nice, yLabels, gridY, points };
}

/**
 * A "step-after" path over the same `[x, y]` points `volumeGeometry` already
 * placed: horizontal to the next point's x, then vertical to its y — a step
 * line instead of a smooth diagonal between points (§5.2's "Step" chart type).
 */
export function stepPath(points: readonly (readonly [number, number])[]): string {
  const first = points[0];
  if (first === undefined) return '';
  let d = `M${first[0].toFixed(1)},${first[1].toFixed(1)}`;
  for (let i = 1; i < points.length; i++) {
    const point = points[i];
    const previous = points[i - 1];
    if (point === undefined || previous === undefined) continue;
    d += ` L${point[0].toFixed(1)},${previous[1].toFixed(1)} L${point[0].toFixed(1)},${point[1].toFixed(1)}`;
  }
  return d;
}

// --------------------------------------------------------------- vertical bar
export interface VerticalBar {
  x: number;
  y: number;
  h: number;
  cx: number;
  vy: number;
  /** Leader (rank 1) is accent; the rest are a-bd. */
  leader: boolean;
}

/** §5.4 — vertical bars: x = 18 + i·58, h = max(4, v/max·88), y = 110 − h. */
export function verticalBars(values: readonly number[]): VerticalBar[] {
  const max = Math.max(...values, 1);
  const leaderIndex = values.indexOf(Math.max(...values));
  return values.map((v, i) => {
    const x = 18 + i * 58;
    const h = Math.max(4, (v / max) * 88);
    return { x, y: 110 - h, h, cx: x + 17, vy: 104 - h, leader: i === leaderIndex };
  });
}

// ----------------------------------------------------------------- donut ----
export interface DonutSlice {
  dash: string;
  off: string;
  frac: number;
  pct: number;
}

/** §5.5 — one full circle per slice: dash = `len 301.6`, off = −accumulated. */
export function donutSlices(values: readonly number[]): DonutSlice[] {
  const total = values.reduce((a, b) => a + b, 0) || 1;
  let acc = 0;
  return values.map((v) => {
    const frac = v / total;
    const len = frac * 301.6;
    const slice: DonutSlice = {
      dash: `${len.toFixed(1)} 301.6`,
      off: (-acc).toFixed(1),
      frac,
      pct: Math.round(frac * 100),
    };
    acc += len;
    return slice;
  });
}

export interface PieSlice {
  d: string;
  frac: number;
  pct: number;
}

/**
 * True pie wedges (centre → arc → centre, closed) for a solid disc centred at
 * `(cx, cy)` with radius `r`. Unlike `donutSlices()`'s stroke-dasharray trick —
 * built for a ring, where each slice is drawn as a full circle's stroke dashed
 * down to its own arc — adjacent wedges here share an exact boundary vertex,
 * so there's no seam for the renderer to anti-alias between slices. That seam
 * is what made a many-category pie (a dozen-plus thin slices) look speckled/
 * "grainy" when drawn with the ring technique instead of true wedges.
 */
export function pieSlices(values: readonly number[], cx = 100, cy = 100, r = 96): PieSlice[] {
  const total = values.reduce((a, b) => a + b, 0) || 1;
  let angle = 0;
  return values.map((v) => {
    const frac = v / total;
    const start = angle;
    const end = angle + frac * Math.PI * 2;
    angle = end;
    const pct = Math.round(frac * 100);
    if (frac <= 0) return { d: '', frac, pct };
    // A single 100% slice has coincident start/end points — one arc command
    // can't render a full circle from a point to itself, so split it in two.
    if (frac >= 1) {
      const left = (cx - r).toFixed(1);
      const right = (cx + r).toFixed(1);
      const y = cy.toFixed(1);
      return {
        d: `M ${left},${y} A ${String(r)},${String(r)} 0 1,1 ${right},${y} A ${String(r)},${String(r)} 0 1,1 ${left},${y} Z`,
        frac,
        pct,
      };
    }
    const x1 = (cx + r * Math.sin(start)).toFixed(1);
    const y1 = (cy - r * Math.cos(start)).toFixed(1);
    const x2 = (cx + r * Math.sin(end)).toFixed(1);
    const y2 = (cy - r * Math.cos(end)).toFixed(1);
    const largeArc = end - start > Math.PI ? 1 : 0;
    return {
      d: `M ${String(cx)},${String(cy)} L ${x1},${y1} A ${String(r)},${String(r)} 0 ${String(largeArc)},1 ${x2},${y2} Z`,
      frac,
      pct,
    };
  });
}

// --------------------------------------------------------------- histogram --
export interface HistogramBar {
  x: number;
  y: number;
  h: number;
  fill: string;
  opacity: number;
}

/** §5.6 — 10 buckets: x = 12 + i·28, h = max(3, v/max·98), y = 112 − h. */
export function histogramBars(hist: readonly number[]): HistogramBar[] {
  const max = Math.max(...hist, 1);
  return hist.map((v, i) => {
    const h = Math.max(3, (v / max) * 98);
    const below = i < 5;
    return {
      x: 12 + i * 28,
      y: 112 - h,
      h,
      fill: below ? 'var(--warn-dot)' : 'var(--accent)',
      opacity: below ? 0.55 : 0.35 + (i - 5) * 0.13,
    };
  });
}

// ---------------------------------------------------------------- trend -----
/** §5.7 — 12 monthly points: x = 6 + i·19, y = 44 − i·slope·2.6 + sin(i·1.7+seed)·3.5. */
export function trendPoints(slope: number, seed: number): string {
  const pts: string[] = [];
  for (let i = 0; i < 12; i++) {
    const y = 44 - i * slope * 2.6 + Math.sin(i * 1.7 + seed) * 3.5;
    pts.push(`${String(6 + i * 19)},${y.toFixed(1)}`);
  }
  return pts.join(' ');
}

// ------------------------------------------------------------ horizontal bar
/** §5.3 — DOM horizontal bars normalised to the LARGEST value, not the total. */
export function horizontalBarWidth(
  value: number,
  values: readonly number[],
  reference?: number,
): string {
  // `reference` overrides the largest value, for a chart holding a row that is
  // not a peer of the others — an "Others" aggregate of everything outside the
  // top N is usually the biggest number present, and scaling to it flattens
  // every real category into a stub. Clamped, so a row above the reference
  // simply fills its track rather than overflowing it.
  const max = Math.max(reference ?? Math.max(...values), 1);
  return `${String(Math.min(100, Math.round((value / max) * 100)))}%`;
}
